// src/schema.ts
import { z } from 'zod';
import { questionDefinitions } from './questions';

// Build base shape dynamically from questionDefinitions
const baseShape: Record<string, z.ZodTypeAny> = {};
questionDefinitions.forEach((q) => {
  baseShape[q.id] = q.validation || z.optional(z.any());
});

const baseSchema = z.object(baseShape);

// Extend with cross‑field conditional validation
export const formSchema = baseSchema.superRefine((data, ctx) => {
  // Helper to check if a value is a non-empty string
  const isNonEmptyString = (val: any): val is string => {
    return typeof val === 'string' && val.trim() !== '';
  };

  // Rule A: Passport type restriction from original.html check_ppt_type()
  if (data.nationality && data.nationality !== 'AFGHANISTAN') {
    if (data.passportType && data.passportType !== 'ORDINARY PASSPORT') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['passportType'],
        message: 'Only ordinary passport holders are allowed for e-Visa service. You may approach concerned Indian Mission abroad.',
      });
    }
  }

  // Rule B: Sri Lanka 5-Year e-Tourist Restriction from original.html show_hide_purpose()
  if (data.nationality === 'SRI LANKA') {
    if (typeof data.visitingIndiaFor === 'string' && data.visitingIndiaFor.includes('5 year')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['visitingIndiaFor'],
        message: 'Please choose a different service as 5-year e-Tourist Visa is not applicable for Sri Lankan passport holders.',
      });
    }
  }

  // 1. If sameAddress === 'NO', permanent address fields are required
  if (data.sameAddress === 'NO') {
    if (!isNonEmptyString(data.permHouseNoStreet)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['permHouseNoStreet'],
        message: 'Permanent house address is required.',
      });
    }
  }

  // 2. If bookedHotel === 'YES', hotel details are required
  if (data.bookedHotel === 'YES') {
    if (!isNonEmptyString(data.hotelName)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['hotelName'],
        message: 'Hotel name is required.',
      });
    }
    if (!isNonEmptyString(data.hotelPlaceCity)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['hotelPlaceCity'],
        message: 'Hotel place/city is required.',
      });
    }
  }

  // 3. If visitedIndiaBefore === 'YES', previous visa details are required
  if (data.visitedIndiaBefore === 'YES') {
    if (!isNonEmptyString(data.prevIndiaAddressStayed)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['prevIndiaAddressStayed'],
        message: 'Previous India address is required.',
      });
    }
    if (!isNonEmptyString(data.prevIndiaCitiesVisited)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['prevIndiaCitiesVisited'],
        message: 'Previous cities visited are required.',
      });
    }
    if (!isNonEmptyString(data.prevIndianVisaNo)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['prevIndianVisaNo'],
        message: 'Previous visa number is required.',
      });
    }
    if (!isNonEmptyString(data.prevVisaType)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['prevVisaType'],
        message: 'Previous visa type is required.',
      });
    }
    if (!isNonEmptyString(data.prevVisaPlaceIssue)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['prevVisaPlaceIssue'],
        message: 'Previous visa place of issue is required.',
      });
    }
    if (!isNonEmptyString(data.prevVisaDateIssue)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['prevVisaDateIssue'],
        message: 'Previous visa date of issue is required.',
      });
    }
  }

  // 4. If visitedSaarc === 'YES', country visited is required
  if (data.visitedSaarc === 'YES') {
    if (!isNonEmptyString(data.saarcCountriesVisited)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['saarcCountriesVisited'],
        message: 'SAARC country visited is required.',
      });
    }
  }

  // 5. If maritalStatus === 'MARRIED', spouse details are required
  if (data.maritalStatus === 'MARRIED') {
    if (!isNonEmptyString(data.spouseName)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['spouseName'],
        message: "Spouse's name is required.",
      });
    }
    if (!isNonEmptyString(data.spouseNationality)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['spouseNationality'],
        message: "Spouse's nationality is required.",
      });
    }
    if (!isNonEmptyString(data.spousePlaceOfBirth)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['spousePlaceOfBirth'],
        message: "Spouse's place of birth is required.",
      });
    }
  }

  // 6. If militaryService === 'YES', organization details required
  if (data.militaryService === 'YES') {
    if (!isNonEmptyString(data.militaryOrganization)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['militaryOrganization'],
        message: 'Military / Security Organization name is required.',
      });
    }
  }

  // 7. If otherPassportIC === 'YES', other passport country required
  if (data.otherPassportIC === 'YES') {
    if (!isNonEmptyString(data.otherPassportCountry)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['otherPassportCountry'],
        message: 'Other Passport / IC country of issue is required.',
      });
    }
    if (!isNonEmptyString(data.otherPassportNo)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['otherPassportNo'],
        message: 'Other Passport / IC number is required.',
      });
    }
  }

  // 8. If visitingIndiaFor includes e-Business, Indian Host Company details are required
  if (typeof data.visitingIndiaFor === 'string' && data.visitingIndiaFor.includes('e-Business')) {
    if (!isNonEmptyString(data.indianCompanyName)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['indianCompanyName'],
        message: 'Name of Indian Host Company / Organization is required for Business Visa.',
      });
    }
    if (!isNonEmptyString(data.indianCompanyAddress)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['indianCompanyAddress'],
        message: 'Address of Indian Host Company / Organization is required for Business Visa.',
      });
    }
  }

  // 9. If visitingIndiaFor includes e-Medical, Indian Hospital details are required
  if (typeof data.visitingIndiaFor === 'string' && (data.visitingIndiaFor.includes('e-Medical') || data.visitingIndiaFor.includes('e-Ayush'))) {
    if (!isNonEmptyString(data.indianHospitalName)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['indianHospitalName'],
        message: 'Name of Hospital in India is required for Medical Visa.',
      });
    }
    if (!isNonEmptyString(data.indianHospitalAddress)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['indianHospitalAddress'],
        message: 'Address of Hospital in India is required for Medical Visa.',
      });
    }
  }
});

export type FormData = z.infer<typeof formSchema>;
